import { useState } from 'react';
import { Link, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, User, Lock, CheckCircle, Zap, Shield, Crown, Star, ChevronDown } from 'lucide-react';
import { Spinner } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { createTrialCheckout, createCheckoutSession, startGoogleSignIn } from '../services/api';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import Seo from '../components/Seo';

function GoogleIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

const PLAN_META = {
  trial:    { label: 'Free Trial',   price: 'Free — no credit card needed', icon: Star,  color: 'text-emerald-400', isTrial: true },
  starter:  { label: 'Starter',      price: '$10/mo (annual) or $19.99/mo', icon: Zap,   color: 'text-blue-400' },
  trader:   { label: 'Trader',       price: '$49.99/mo (annual) or $65.99/mo', icon: Shield, color: 'text-cyan-400' },
  pro:      { label: 'Pro',          price: '$75.99/mo (annual) or $99.99/mo', icon: Crown, color: 'text-violet-400' },
  elite:    { label: 'Elite',        price: '$149.99/mo (annual) or $199.99/mo', icon: Crown, color: 'text-accent-primary' },
};

const PASSWORD_RULES = [
  { id: 'length',  label: 'At least 8 characters', test: p => p.length >= 8 },
  { id: 'upper',   label: 'One uppercase letter',  test: p => /[A-Z]/.test(p) },
  { id: 'number',  label: 'One number',             test: p => /\d/.test(p) },
];

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan') || 'trial';
  const paymentPath = searchParams.get('path') || 'trial'; // 'trial', 'stripe', 'usdc'
  const referredBy = searchParams.get('ref') || null;
  const meta = PLAN_META[selectedPlan] || PLAN_META.trial;
  const PlanIcon = meta.icon;
  const isTrial = paymentPath === 'trial' || selectedPlan === 'trial';

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const { register, isLoading, error, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const handleGoogle = async () => {
    setGoogleError(null); setGoogleBusy(true);
    try { await startGoogleSignIn('/'); }
    catch (e) { setGoogleError(e.message || 'Google sign-up failed'); setGoogleBusy(false); }
  };

  // If already logged in and not mid-redirect to Stripe, send to dashboard
  if (isLoggedIn && !redirecting) return <Navigate to="/" replace />;

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const passwordValid = PASSWORD_RULES.every(r => r.test(form.password));
  const canSubmit = form.name && form.email && passwordValid && agreed;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setRedirecting(true);
    const result = await register({ email: form.email, name: form.name, password: form.password, referredBy });
    if (!result.success) { setRedirecting(false); return; }

    if (paymentPath === 'trial' && selectedPlan !== 'trial') {
      // Trial with card capture — Stripe checkout with 14-day trial
      try {
        const data = await createTrialCheckout(selectedPlan, 'monthly');
        if (data?.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
        console.error('No checkoutUrl in trial response:', data);
      } catch (err) {
        console.error('Trial checkout error:', err);
        alert(`Checkout error: ${err.message || 'Unknown error'}. Redirecting to settings.`);
      }
      navigate(`/settings?tab=subscription&newUser=1&plan=${selectedPlan}`);
      return;
    }

    if (paymentPath === 'stripe') {
      // Direct Stripe payment — no trial
      try {
        const data = await createCheckoutSession(selectedPlan, 'monthly');
        if (data?.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
        console.error('No checkoutUrl in response:', data);
      } catch (err) {
        console.error('Stripe checkout error:', err);
        alert(`Checkout error: ${err.message || 'Unknown error'}. Redirecting to settings.`);
      }
      navigate(`/settings?tab=subscription&newUser=1&plan=${selectedPlan}`);
      return;
    }

    if (paymentPath === 'usdc') {
      // Crypto payment — bounce to Settings which will auto-open the PayTierWithCrypto modal
      navigate(`/settings?tab=subscription&newUser=1&plan=${selectedPlan}&path=usdc`);
      return;
    }

    // Free trial (no plan from pricing) — go to dashboard
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <Seo
        title="Sign up — 14-day free trial, all features"
        description="Create your haythix account. 14-day free trial with full access to multi-chain DEX + CEX bots, copy trading, AI signals, and 147 tools. No card required."
        path="/register"
      />
      <PublicNav />
      {/* Background glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-3/4 h-3/4 bg-gradient-radial from-accent-primary/4 to-transparent rounded-full" />
        <div className="absolute -bottom-1/2 -left-1/4 w-3/4 h-3/4 bg-gradient-radial from-accent-secondary/4 to-transparent rounded-full" />
      </div>

      <div className="relative flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-dark-700 border border-dark-500 mb-3 shadow-glow-gold">
              <img src={logo} alt="haythix" className="w-11 h-11" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-white">Create your account</h1>
          <p className="text-gray-400 mt-1 text-sm">Start trading smarter with haythix</p>
        </div>

        {/* Honesty-positioned reassurance — describes what we publish, not what users will earn.
            No outcome claims. */}
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 mb-5 text-xs text-gray-300 text-center">
          You're joining a platform that publishes its methodology.{' '}
          <Link to="/comparison" className="text-emerald-300 hover:underline">7 Verticals · 6 Phases</Link>,
          each backed by a public API anyone can read. Not financial advice. Tools, not guarantees.
        </div>

        {/* Selected plan pill + inline picker */}
        <div className="flex flex-col items-center mb-6">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${
            paymentPath === 'usdc' ? 'bg-emerald-500/10 border-emerald-500/30' :
            paymentPath === 'stripe' ? 'bg-blue-500/10 border-blue-500/30' :
            'bg-emerald-500/10 border-emerald-500/30'
          }`}>
            <PlanIcon className={`w-4 h-4 ${meta.color}`} />
            <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
            {paymentPath === 'trial' && <span className="text-[10px] font-bold bg-emerald-500 text-dark-900 px-1.5 py-0.5 rounded ml-1">14-DAY TRIAL</span>}
            {paymentPath === 'stripe' && <span className="text-[10px] font-bold bg-blue-500 text-white px-1.5 py-0.5 rounded ml-1">💳 CARD</span>}
            {paymentPath === 'usdc' && <span className="text-[10px] font-bold bg-emerald-500 text-dark-900 px-1.5 py-0.5 rounded ml-1">💰 USDC</span>}
          </div>
        </div>
        <p className="text-center text-xs text-gray-400 mb-4 -mt-3">
          {paymentPath === 'trial' ? 'Credit card required · No charge for 14 days · Auto-converts to paid' :
           paymentPath === 'stripe' ? `Pay $${isNaN(meta.price) ? '' : ''}now with credit card · Immediate access` :
           paymentPath === 'usdc' ? 'Pay with USDC · Immediate access · Manual renewal' :
           'No credit card required · 14-day free access · Upgrade anytime'}
        </p>

        {/* Registration form */}
        <div className="card border-dark-500">
          {/* Google OAuth — additive, not replacing email/password */}
          {isTrial && (
            <>
              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleBusy}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed font-medium text-sm py-2.5 rounded-lg border border-gray-200 transition-colors"
              >
                {googleBusy ? <Spinner size="sm" /> : <GoogleIcon className="w-5 h-5" />}
                <span>{googleBusy ? 'Redirecting…' : 'Sign up with Google'}</span>
              </button>
              {googleError && (
                <p className="mt-2 text-xs text-red-400 text-center">{googleError}</p>
              )}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-dark-600" />
                <span className="text-[10px] uppercase tracking-widest text-gray-500">or sign up with email</span>
                <div className="flex-1 h-px bg-dark-600" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  className="input pl-10"
                  placeholder="Jane Doe"
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  className="input pl-10"
                  placeholder="jane@example.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={update('password')}
                  className="input pl-10 pr-10"
                  placeholder="Create a strong password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Password strength indicators */}
              {form.password && (
                <div className="mt-2 space-y-1">
                  {PASSWORD_RULES.map(rule => (
                    <div key={rule.id} className="flex items-center gap-1.5">
                      <CheckCircle className={`w-3 h-3 ${rule.test(form.password) ? 'text-emerald-400' : 'text-dark-500'}`} />
                      <span className={`text-xs ${rule.test(form.password) ? 'text-emerald-400' : 'text-gray-600'}`}>
                        {rule.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Terms */}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 accent-accent-primary"
              />
              <span className="text-xs text-gray-400 leading-relaxed">
                I agree to the{' '}
                {/* In-app navigation (no target="_blank") — installed PWAs treat new-tab links
                    inconsistently and flash before snapping back. Inline nav is predictable;
                    the browser back button returns the user to this form. */}
                <Link to="/terms" className="text-accent-primary hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-accent-primary hover:underline">
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* Error */}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || redirecting || !canSubmit}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3"
            >
              {redirecting ? (
                <>
                  <Spinner size="sm" />
                  Redirecting to checkout…
                </>
              ) : isLoading ? (
                <>
                  <Spinner size="sm" />
                  Creating account…
                </>
              ) : paymentPath === 'trial' ? (
                'Create Account & Start Trial'
              ) : paymentPath === 'stripe' ? (
                'Create Account & Pay with Card'
              ) : paymentPath === 'usdc' ? (
                'Create Account & Pay with USDC'
              ) : (
                'Create Free Account'
              )}
            </button>
          </form>
        </div>

        {/* Footer links */}
        <div className="text-center mt-6 space-y-3">
          <p className="text-gray-500 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-accent-primary hover:underline">
              Sign in
            </Link>
          </p>
          <p className="text-gray-600 text-xs">
            Want to see paid plans?{' '}
            <Link to="/pricing" className="text-accent-primary hover:underline">
              View Pricing
            </Link>
          </p>
          <p className="text-gray-600 text-xs">
            © {new Date().getFullYear()} Borak Solutions LLC. All rights reserved.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
